/**
 * Menu Manage — Global clipboard context for copy-paste workflow.
 *
 * Supports copying Items, OptionGroups, single Options, and bulk Options.
 * The clipboard stores a deep-cloned snapshot so the original can be
 * modified without affecting the paste.
 */

import React, { createContext, useContext, useState, useCallback } from 'react';
import type { MenuItem, OptionGroup, MenuItemOption } from './types';

// ── Types ────────────────────────────────────────────────────────────────────

export type ClipboardType = 'item' | 'optionGroup' | 'option' | 'options';

export interface ClipboardPayload {
  type: ClipboardType;
  label: string;
  /** Deep-cloned snapshot of the copied object (IDs preserved — caller must re-ID on paste). */
  data: MenuItem | OptionGroup | MenuItemOption | MenuItemOption[];
}

interface ClipboardContextValue {
  clipboard: ClipboardPayload | null;
  copyToClipboard: (payload: ClipboardPayload) => void;
  clearClipboard: () => void;
}

// ── Context ──────────────────────────────────────────────────────────────────

const ClipboardContext = createContext<ClipboardContextValue>({
  clipboard: null,
  copyToClipboard: () => {},
  clearClipboard: () => {},
});

export function useMenuClipboard() {
  return useContext(ClipboardContext);
}

// ── Provider ─────────────────────────────────────────────────────────────────

export function MenuClipboardProvider({ children }: { children: React.ReactNode }) {
  const [clipboard, setClipboard] = useState<ClipboardPayload | null>(null);

  const copyToClipboard = useCallback((payload: ClipboardPayload) => {
    setClipboard(payload);
  }, []);

  const clearClipboard = useCallback(() => {
    setClipboard(null);
  }, []);

  return (
    <ClipboardContext.Provider value={{ clipboard, copyToClipboard, clearClipboard }}>
      {children}
    </ClipboardContext.Provider>
  );
}
